import {
  useClickOutside as useMantineClickOutside,
  useDebouncedState as useMantineDebouncedState,
  useElementSize as useMantineElementSize,
  useHotkeys as useMantineHotkeys,
  useId as useMantineId,
  useLocalStorage as useMantineLocalStorage,
  useMediaQuery as useMantineMediaQuery,
  useSessionStorage as useMantineSessionStorage,
  useTimeout as useMantineTimeout,
} from "@mantine/hooks";
import { type MutableRefObject, type RefObject, type SetStateAction, useCallback, useEffect, useReducer, useRef, useState } from "react";

export const randomId = (): string => `mantine-${Math.random().toString(36).slice(2, 11)}`;

export type ToggleState<T> = readonly [T, (value?: SetStateAction<T>) => void];

const DEFAULT_TOGGLE_OPTIONS: readonly boolean[] = [false, true];

export function useToggle(): ToggleState<boolean>;
export function useToggle<T>(options: readonly T[]): ToggleState<T>;
export function useToggle<T>(options?: readonly T[]): ToggleState<T | boolean> {
  const initialOptions = [...(options ?? DEFAULT_TOGGLE_OPTIONS)];
  const [state, dispatch] = useReducer(
    (current: { options: (T | boolean)[]; value: T | boolean }, action: SetStateAction<T | boolean> | undefined) => {
      const index =
        action === undefined ? 1 : Math.abs(current.options.indexOf(action instanceof Function ? action(current.value) : action));
      const nextOptions = [...current.options.slice(index), ...current.options.slice(0, index)];
      return { options: nextOptions, value: nextOptions[0] ?? current.value };
    },
    { options: initialOptions, value: initialOptions[0] ?? false },
  );
  const toggle = useCallback((value?: SetStateAction<T | boolean>) => dispatch(value), [dispatch]);

  return [state.value, toggle];
}

export type HoverState<T extends HTMLElement> = { ref: RefObject<T>; hovered: boolean };

export const useHover = <T extends HTMLElement = HTMLDivElement>(): HoverState<T> => {
  const [hovered, setHovered] = useState(false);
  const ref = useRef<T>(null);
  const onMouseEnter = useCallback(() => setHovered(true), []);
  const onMouseLeave = useCallback(() => setHovered(false), []);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    element.addEventListener("mouseenter", onMouseEnter);
    element.addEventListener("mouseleave", onMouseLeave);
    return () => {
      element.removeEventListener("mouseenter", onMouseEnter);
      element.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [onMouseEnter, onMouseLeave]);

  return { ref, hovered };
};

export type DebouncedState<T> = readonly [T];

export const useDebouncedState = <T>(defaultValue: T, wait: number): DebouncedState<T> => {
  const [value] = useMantineDebouncedState(defaultValue, wait);
  return [value];
};

/**
 * Mantine 7 types the returned ref as `RefObject<T | null>` where Mantine 6 typed it
 * `MutableRefObject<T>`; both return the same plain ref object, which the caller attaches through
 * `ref`. The owned return type is unchanged (SC-2703). AGENTS.md D01b: hooks.test.ts proves the
 * object is the live ref the handler reads — attached by the caller, firing outside and not inside.
 */
export const useClickOutside = <T extends HTMLElement = HTMLDivElement>(handler: () => void): MutableRefObject<T> =>
  useMantineClickOutside<T>(handler) as MutableRefObject<T>;

export type TimeoutControls = { start: () => void };

/**
 * Mantine 7's `start` calls the callback it was created with; Mantine 6's read the newest one through a
 * ref. The facade keeps the Mantine 6 behaviour (SC-2703): the timer fires whatever `callback` the
 * last render passed.
 */
export const useTimeout = (callback: () => void, delay: number): TimeoutControls => {
  const latestCallback = useRef(callback);
  useEffect(() => {
    latestCallback.current = callback;
  }, [callback]);

  const { start } = useMantineTimeout(() => latestCallback.current(), delay);
  return { start };
};

export type Hotkey = [key: string, handler: (event: KeyboardEvent) => void];

/**
 * Mantine 7 matches a hotkey against a normalised key name, and names the Escape key "esc", so a
 * Mantine 6 `"Escape"` hotkey would never fire there. The facade spells it the Mantine 7 way; every
 * other key name Mantine 6 matched by `event.key` normalises identically (SC-2703).
 */
export const toMantine7Hotkey = (hotkey: string): string =>
  hotkey
    .split("+")
    .map(part => (part.trim().toLowerCase() === "escape" ? "esc" : part))
    .join("+");

export const useHotkeys = (hotkeys: Hotkey[]): void =>
  useMantineHotkeys(hotkeys.map(([hotkey, handler]): Hotkey => [toMantine7Hotkey(hotkey), handler]));

/**
 * Mantine 7 declares `boolean | undefined`: the first render, before the effect reads the query, is
 * undefined — as it was in Mantine 6, whose type said `boolean`. The owned contract is `boolean`, so
 * that render reads `false` (SC-2703); every call site treats the value as a flag.
 */
export const useMediaQuery = (query: string): boolean => useMantineMediaQuery(query) ?? false;

export const useId = (): string => useMantineId();

export type StorageOptions<T> = {
  key: string;
  defaultValue: T;
  /**
   * Reads the stored value during the first render instead of after paint, for state that would otherwise
   * visibly jump once storage is read. Storage access and JSON errors fall back to the default.
   */
  readOnMount?: boolean;
};
export type StorageState<T> = readonly [T, (value: T | ((currentValue: T) => T)) => void];

/** A stored value tagged with the key it was read for. */
type KeyedValue<T> = { readonly key: string; readonly value: T };

/** The part of Mantine's local/session storage hook signature the facade uses. */
type VendorStorageHook = <S>(options: {
  key: string;
  defaultValue: S;
  serialize: (value: S) => string;
  deserialize: (raw: string | undefined) => S;
}) => readonly [S, (value: S | ((current: S) => S)) => void, ...unknown[]];

const isStorageUpdater = <T>(next: T | ((currentValue: T) => T)): next is (currentValue: T) => T => typeof next === "function";

/** Mantine's own JSON reader: a value that does not parse is returned as the raw string. */
const parseStored = (raw: string) => {
  try {
    return raw && JSON.parse(raw);
  } catch {
    return raw;
  }
};

type StorageType = "localStorage" | "sessionStorage";

/** The stored value read synchronously, or the default when nothing parses or storage cannot be reached. */
const readStorageOnMount = <T>(storageType: StorageType, key: string, defaultValue: T): T => {
  try {
    const raw = window[storageType].getItem(key);
    return raw === null ? defaultValue : JSON.parse(raw);
  } catch {
    return defaultValue;
  }
};

/**
 * Mantine 6 only read storage: a key with nothing stored rendered its default and wrote nothing.
 * Mantine 7 writes the stored-or-default value back whenever the key mounts, which freezes each
 * default into storage on a first visit — managed table columns would then ignore every later change
 * to their default order or widths. The facade therefore gives Mantine no default, so the only value
 * Mantine 7 can write back is one already stored, and applies the default itself (SC-2703). Mantine 7
 * also keeps the previous key's value when a new key has nothing stored; tagging each read with its key
 * lets the facade fall back to the default there, as Mantine 6 did. Storage keeps Mantine's plain JSON.
 */
const useKeyedStorage = <T>(
  useVendorStorage: VendorStorageHook,
  storageType: StorageType,
  { key, defaultValue, readOnMount = false }: StorageOptions<T>,
): StorageState<T> => {
  // Mantine reads storage in an effect, after the first paint; until it delivers, the mount read stands in.
  const [mountRead] = useState<KeyedValue<T> | undefined>(() =>
    readOnMount ? { key, value: readStorageOnMount(storageType, key, defaultValue) } : undefined,
  );
  const [stored, setStored] = useVendorStorage<KeyedValue<T> | undefined>({
    key,
    defaultValue: undefined,
    serialize: keyed => JSON.stringify(keyed?.value),
    deserialize: raw => (raw === undefined ? undefined : { key, value: parseStored(raw) }),
  });
  const fallbackValue = mountRead?.key === key ? mountRead.value : defaultValue;
  const fallbackValueRef = useRef(fallbackValue);
  fallbackValueRef.current = fallbackValue;

  const setValue = useCallback(
    (next: T | ((currentValue: T) => T)) =>
      setStored(current => {
        const currentValue = current?.key === key ? current.value : fallbackValueRef.current;
        return { key, value: isStorageUpdater(next) ? next(currentValue) : next };
      }),
    [key, setStored],
  );

  return [stored?.key === key ? stored.value : fallbackValue, setValue];
};

export const useLocalStorage = <T>(options: StorageOptions<T>): StorageState<T> =>
  useKeyedStorage(useMantineLocalStorage, "localStorage", options);

export const useSessionStorage = <T>(options: StorageOptions<T>): StorageState<T> =>
  useKeyedStorage(useMantineSessionStorage, "sessionStorage", options);

export type ElementSize<T extends HTMLElement> = { ref: MutableRefObject<T>; width: number };

/**
 * Mantine 7 types the ref as `RefObject<T | null>` where Mantine 6 typed it `MutableRefObject<T>`; both
 * return the plain ref object the observer reads once the caller attaches it. The owned type is
 * unchanged (SC-2703). AGENTS.md D01b: hooks.test.ts proves the returned object is that live ref.
 */
export const useElementSize = <T extends HTMLElement = HTMLDivElement>(): ElementSize<T> => {
  const { ref, width } = useMantineElementSize<T>();
  return { ref: ref as MutableRefObject<T>, width };
};

export type DisclosureAction = "open" | "close" | "toggle";

export interface DisclosureHandlers {
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export type DisclosureState = readonly [opened: boolean, handlers: DisclosureHandlers];

export const getInitialDisclosureState = (initialState = false): boolean => initialState;

/** The state policy is separate so its idempotence remains executable without rendering. */
export const nextDisclosureState = (opened: boolean, action: DisclosureAction): boolean => {
  switch (action) {
    case "open":
      return true;
    case "close":
      return false;
    case "toggle":
      return !opened;
  }
};

/** ShelfCycle-owned disclosure state; current callers use no vendor callbacks. */
export const useDisclosure = (initialState = false): DisclosureState => {
  const [opened, setOpened] = useState(getInitialDisclosureState(initialState));
  const open = useCallback(() => setOpened(current => nextDisclosureState(current, "open")), []);
  const close = useCallback(() => setOpened(current => nextDisclosureState(current, "close")), []);
  const toggle = useCallback(() => setOpened(current => nextDisclosureState(current, "toggle")), []);

  return [opened, { open, close, toggle }];
};

export type DebounceSchedule<T> = { shouldSchedule: false } | { shouldSchedule: true; value: T; wait: number };

/** The hook consumes this transition directly; the first caller value stays immediate. */
export const getDebounceSchedule = <T>(hasMounted: boolean, value: T, wait: number): DebounceSchedule<T> =>
  hasMounted ? { shouldSchedule: true, value, wait } : { shouldSchedule: false };

/** ShelfCycle-owned trailing debounce; current callers consume only the debounced value tuple entry. */
export const useDebouncedValue = <T>(value: T, wait: number): readonly [T] => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const hasMounted = useRef(false);

  useEffect(() => {
    const schedule = getDebounceSchedule(hasMounted.current, value, wait);
    if (!schedule.shouldSchedule) {
      return;
    }

    const timeout = window.setTimeout(() => setDebouncedValue(schedule.value), schedule.wait);
    return () => window.clearTimeout(timeout);
  }, [value, wait]);

  useEffect(() => {
    hasMounted.current = true;
    return () => {
      hasMounted.current = false;
    };
  }, []);

  return [debouncedValue];
};
